/**
 * Cognito sign-in, wrapped in promises.
 *
 * `amazon-cognito-identity-js` rather than the Hosted UI: this is one
 * household's budget, so a redirect to an AWS-branded login page for a
 * two-person audience is worse than a form that looks like the rest of the app.
 * SRP means the password is never sent over the wire.
 *
 * Tokens live in localStorage, which is where the SDK puts them. That is a real
 * XSS exposure and the mitigation is that this app renders no third-party
 * script and no user-supplied HTML.
 */

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { config } from '../config';

/*
 * Built on first use, not at import. `CognitoUserPool` validates its ids in the
 * constructor, and a throw during module evaluation would take down the app
 * before it could explain that the configuration is missing.
 */
let cachedPool: CognitoUserPool | undefined;

function pool(): CognitoUserPool {
  cachedPool ??= new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.userPoolClientId,
  });
  return cachedPool;
}

export interface Session {
  accessToken: string;
  email: string;
  expiresAt: number;
}

/** Cognito's own error shapes, mapped to something a person can act on. */
export class SignInError extends Error {
  constructor(
    message: string,
    readonly code: string
  ) {
    super(message);
    this.name = 'SignInError';
  }
}

/**
 * A challenge the user has to clear before there is a session. Cognito requires
 * a new password on a freshly created account.
 */
export class NewPasswordRequiredError extends Error {
  constructor(readonly user: CognitoUser) {
    super('A new password is required for this account.');
    this.name = 'NewPasswordRequiredError';
  }
}

/**
 * @param typed What the person typed to sign in, when there is such a thing.
 *   On a restored session there is not, and the pool's own username is a UUID —
 *   email is a sign-in *alias* here, so `getUsername()` returns the `sub`. The
 *   id token's `email` claim is the only place the address reliably lives, which
 *   is why it wins over both.
 */
function toSession(session: CognitoUserSession, typed?: string): Session {
  const accessToken = session.getAccessToken();
  const claimed = session.getIdToken().payload.email;
  return {
    accessToken: accessToken.getJwtToken(),
    email: (typeof claimed === 'string' && claimed) || typed || '',
    // Cognito reports seconds; the rest of the app works in milliseconds.
    expiresAt: accessToken.getExpiration() * 1000,
  };
}

function friendlyMessage(code: string, fallback: string): string {
  switch (code) {
    case 'NotAuthorizedException':
      return 'That email and password do not match. Check both and try again.';
    case 'UserNotFoundException':
      // preventUserExistenceErrors should stop this reaching us, but be safe.
      return 'That email and password do not match. Check both and try again.';
    case 'PasswordResetRequiredException':
      return 'This account needs a new password. Ask for a reset to continue.';
    case 'TooManyRequestsException':
    case 'LimitExceededException':
      return 'Too many attempts. Wait a minute, then try again.';
    case 'NetworkError':
      return 'Could not reach the sign-in service. Check your connection.';
    default:
      return fallback || 'Sign-in failed. Try again.';
  }
}

export function signIn(email: string, password: string): Promise<Session> {
  const user = new CognitoUser({ Username: email, Pool: pool() });
  const details = new AuthenticationDetails({ Username: email, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => resolve(toSession(session, email)),
      onFailure: (error: { code?: string; message?: string }) => {
        const code = error.code ?? 'UnknownError';
        reject(new SignInError(friendlyMessage(code, error.message ?? ''), code));
      },
      newPasswordRequired: () => reject(new NewPasswordRequiredError(user)),
    });
  });
}

/** Completes the `newPasswordRequired` challenge from a first sign-in. */
export function completeNewPassword(
  user: CognitoUser,
  newPassword: string,
  email: string
): Promise<Session> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: (session) => resolve(toSession(session, email)),
      onFailure: (error: { code?: string; message?: string }) => {
        const code = error.code ?? 'UnknownError';
        reject(
          new SignInError(
            code === 'InvalidPasswordException'
              ? 'That password does not meet the requirements below.'
              : friendlyMessage(code, error.message ?? ''),
            code
          )
        );
      },
    });
  });
}

/**
 * The current session, refreshing it if the access token has expired.
 *
 * `getSession` does the refresh-token dance itself, so this doubles as "restore
 * my session on page load".
 */
export function currentSession(): Promise<Session | null> {
  const user = pool().getCurrentUser();
  if (!user) return Promise.resolve(null);

  return new Promise((resolve) => {
    user.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error || !session?.isValid()) {
        resolve(null);
        return;
      }
      resolve(toSession(session));
    });
  });
}

export function signOut(): void {
  pool().getCurrentUser()?.signOut();
}

export function requestPasswordReset(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: pool() });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (error: { code?: string; message?: string }) =>
        reject(
          new SignInError(
            friendlyMessage(error.code ?? 'UnknownError', error.message ?? ''),
            error.code ?? 'UnknownError'
          )
        ),
    });
  });
}

export function confirmPasswordReset(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: pool() });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (error: { code?: string; message?: string }) =>
        reject(
          new SignInError(
            error.code === 'CodeMismatchException'
              ? 'That code is not right. Check the email and try again.'
              : friendlyMessage(error.code ?? 'UnknownError', error.message ?? ''),
            error.code ?? 'UnknownError'
          )
        ),
    });
  });
}
